/**
 * SA bank account validation for portal manual entry (mirrors saBankAccountValidation.js).
 * Length + format only — not bank-specific CDV (Capitec etc. use different rules).
 */

import { resolveCanonicalBankName, type SaBankName } from '../constants/saBanks';

const BANK_ACCOUNT_RULES: Record<
  SaBankName,
  { min: number; max: number; label: string }
> = {
  ABSA: { min: 9, max: 10, label: '9 or 10' },
  FNB: { min: 11, max: 11, label: '11' },
  Nedbank: { min: 10, max: 10, label: '10' },
  'Standard Bank': { min: 9, max: 11, label: '9 or 11' },
  Capitec: { min: 10, max: 10, label: '10' },
  'African Bank': { min: 10, max: 11, label: '10 or 11' },
  'Discovery Bank': { min: 10, max: 11, label: '10 or 11' },
  Investec: { min: 11, max: 11, label: '11' },
  TymeBank: { min: 10, max: 11, label: '10 or 11' },
  'Bidvest Bank': { min: 10, max: 11, label: '10 or 11' },
  Sasfin: { min: 10, max: 11, label: '10 or 11' },
  'Bank Zero': { min: 10, max: 11, label: '10 or 11' },
  'Old Mutual': { min: 10, max: 11, label: '10 or 11' },
};

const BANK_ACCOUNT_IS_MOBILE_ERROR =
  'This looks like a cellphone number, not a bank account number. Enter the account number from your bank app (not your mobile number). The payment was not sent.';

const INSTANT_PAYMENT_CARD_PAN_ERROR =
  'This looks like a card number, not a bank account number. Instant Payment needs the account number from your bank app (not the number on the plastic). The payment was not sent.';

function digitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

function luhnValid(digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = Number(digits[i]);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

function isLikelyCardPan(accountNumber: string): boolean {
  const digits = digitsOnly(accountNumber);
  if (digits.length < 13 || digits.length > 19) return false;
  return luhnValid(digits);
}

export function isLikelySaMobileMsisdn(value?: string | null): boolean {
  const digits = digitsOnly(String(value || ''));
  if (/^0[6-8]\d{8}$/.test(digits)) return true;
  if (/^27[6-8]\d{8}$/.test(digits)) return true;
  return false;
}

export function validateSaBankAccountNumber(
  accountNumber: string,
  bankName: string | null | undefined,
): string | null {
  const digits = String(accountNumber || '').replace(/\s/g, '');
  if (!digits) return 'Account number is required';
  if (isLikelySaMobileMsisdn(digits) || isLikelySaMobileMsisdn(accountNumber)) {
    return BANK_ACCOUNT_IS_MOBILE_ERROR;
  }
  if (!/^\d+$/.test(digits)) return 'Account number must contain digits only';
  if (/^(\d)\1+$/.test(digits)) return 'Account number does not look valid';
  if (isLikelyCardPan(digits)) return INSTANT_PAYMENT_CARD_PAN_ERROR;

  const canonical = bankName ? resolveCanonicalBankName(bankName) : null;
  const rules = canonical ? BANK_ACCOUNT_RULES[canonical] : null;

  if (rules) {
    if (digits.length < rules.min || digits.length > rules.max) {
      return `${canonical} accounts use ${rules.label} digits (you entered ${digits.length})`;
    }
  } else if (digits.length < 6 || digits.length > 20) {
    return 'Account number must be 6–20 digits';
  }

  return null;
}

export function accountNumberHint(bankName: string | null | undefined): string | null {
  const canonical = bankName ? resolveCanonicalBankName(bankName) : null;
  if (!canonical) return null;
  return `${canonical} accounts: ${BANK_ACCOUNT_RULES[canonical].label} digits`;
}
